import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Camera, Loader2, Trash2, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { laporanKegiatanAPI, API } from "../lib/api";

export const KATEGORI_LABEL = { satpam: "Satpam", tukang_taman: "Tukang Taman", petugas_sampah: "Petugas Sampah" };

const LaporanKegiatanDialog = ({ open, onOpenChange, prefill }) => {
  const [items, setItems] = useState([]);
  const [deskripsi, setDeskripsi] = useState("");
  const [foto, setFoto] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => {
    if (!prefill?.tanggal || !prefill?.kategori) return;
    laporanKegiatanAPI.list({ tanggal: prefill.tanggal, kategori: prefill.kategori }).then(setItems).catch(() => {});
  };
  useEffect(() => {
    if (open) { setDeskripsi(""); setFoto([]); load(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefill?.tanggal, prefill?.kategori]);

  const uploadFoto = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        if (f.size > 5 * 1024 * 1024) { toast.error(`${f.name} melebihi 5MB`); continue; }
        const fd = new FormData();
        fd.append("file", f);
        const r = await API.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(r.data.url);
      }
      setFoto((prev) => [...prev, ...urls]);
    } catch (err) { toast.error(err.response?.data?.detail || "Upload gagal"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const save = async () => {
    if (!deskripsi.trim()) { toast.error("Deskripsi kegiatan wajib diisi"); return; }
    setSaving(true);
    try {
      await laporanKegiatanAPI.add({ ...prefill, deskripsi: deskripsi.trim(), foto });
      toast.success("Laporan tersimpan — tampil di Beranda hari ini");
      setDeskripsi(""); setFoto([]); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus laporan ini?")) return;
    try { await laporanKegiatanAPI.remove(id); toast.success("Dihapus"); load(); }
    catch { toast.error("Gagal menghapus"); }
  };

  if (!prefill) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-emerald-700" />
            Laporan Kegiatan — {KATEGORI_LABEL[prefill.kategori] || prefill.kategori}
          </DialogTitle>
          <DialogDescription className="sr-only">Form input laporan kegiatan harian petugas</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-900 flex items-center gap-2 flex-wrap">
            <Badge className="bg-emerald-600 text-white hover:bg-emerald-600">{prefill.tanggal}</Badge>
            {prefill.jam && <span className="font-mono">{prefill.jam} WIB</span>}
            <span className="font-bold">{prefill.petugas_nama}</span>
          </div>

          {items.length > 0 && (
            <div className="space-y-2" data-testid="laporan-existing-list">
              <div className="text-xs font-bold text-slate-600 uppercase tracking-wider">Laporan terkirim ({items.length})</div>
              {items.map((l) => (
                <div key={l.id} className="rounded-lg border bg-white p-2.5 flex gap-2 items-start">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-slate-800">{l.deskripsi}</div>
                    {l.foto?.length > 0 && (
                      <div className="flex gap-1 mt-1.5 overflow-x-auto">
                        {l.foto.map((f, i) => <img key={i} src={f} alt="" className="h-12 w-12 rounded object-cover border shrink-0" />)}
                      </div>
                    )}
                  </div>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 shrink-0" onClick={() => remove(l.id)} data-testid={`laporan-delete-${l.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              ))}
            </div>
          )}

          <div>
            <Label>Deskripsi Kegiatan / Pekerjaan *</Label>
            <Textarea rows={3} value={deskripsi} onChange={(e) => setDeskripsi(e.target.value)} placeholder="Contoh: Patroli keliling blok A-F, aman terkendali..." data-testid="laporan-deskripsi-input" />
          </div>
          <div>
            <Label>Foto Kegiatan (opsional, bisa lebih dari 1)</Label>
            <div className="grid grid-cols-4 gap-2 mt-1">
              {foto.map((f, i) => (
                <div key={i} className="relative aspect-square rounded-md overflow-hidden border group">
                  <img src={f} className="w-full h-full object-cover" alt="" />
                  <button onClick={() => setFoto(foto.filter((_, j) => j !== i))} className="absolute top-1 right-1 h-5 w-5 rounded-full bg-red-600 text-white grid place-items-center opacity-0 group-hover:opacity-100"><Trash2 className="h-3 w-3" /></button>
                </div>
              ))}
              <label className={`aspect-square rounded-md border-2 border-dashed border-emerald-300 hover:bg-emerald-50 grid place-items-center cursor-pointer ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
                <input type="file" accept="image/*" multiple className="hidden" onChange={uploadFoto} disabled={uploading} data-testid="laporan-foto-upload" />
                {uploading ? <Loader2 className="h-5 w-5 animate-spin text-emerald-600" /> : <Camera className="h-5 w-5 text-emerald-600" />}
              </label>
            </div>
          </div>
          <Button className="brand-btn w-full" onClick={save} disabled={saving || uploading} data-testid="laporan-save-btn">
            {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Menyimpan...</> : "Kirim Laporan ke Beranda"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LaporanKegiatanDialog;
